"use client";
import React, { type PropsWithChildren } from "react";
import {
  MutationCache,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { useIsDesktopLayout } from "~/src/hooks/common/useIsDesktopLayout";

import { assertOnline } from "../utils/network";

export const queryClient = new QueryClient({
  mutationCache: new MutationCache({ onMutate: assertOnline }),
  defaultOptions: {
    mutations: { networkMode: "always", retry: false },
    queries: {
      networkMode: "online",
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 5, // 5 minutes
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      retry: (failureCount, error: any) => {
        const errorMessage = String(error?.message ?? "");
        if (errorMessage.includes("429")) {
          return false;
        }

        return failureCount < 2;
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    },
  },
});

export const ReactQueryProvider = ({ children }: PropsWithChildren): any => {
  const isDesktop = useIsDesktopLayout();

  return (
    <QueryClientProvider client={queryClient}>
      {import.meta.env.DEV && isDesktop ? (
        <ReactQueryDevtools initialIsOpen={false} />
      ) : null}
      {children}
    </QueryClientProvider>
  );
};
